"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { generateVoiceIsolation } from "@/app/_api/generateVoiceIsolation";
import { useNativeAudio } from "@/app/_providers/native-audio";
import { getErrorStatusFromUnknownError, handleMicrophoneError } from "@utilities/errors";
import { logger } from "@utilities/logger";
import { AudioRecorder } from "@utilities/audioRecorder";
import { useAutoDismissError } from "@/app/_ui/hooks/useAutoDismissError";

export const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
const MAX_RECORDING_DURATION = 60; // seconds

export type VoiceIsolateStep = "input" | "recording" | "isolating" | "result";

export interface UseVoiceIsolateReturn {
  // State
  step: VoiceIsolateStep;
  duration: number | null;
  isPlaying: boolean;
  isIsolating: boolean;
  originalAudioUrl: string | null;
  isolatedAudioUrl: string | null;
  activeAudioType: "original" | "isolated";
  showLimitReachedOverlay: boolean;
  error: string | null;

  // Actions
  handleFileSelected: (file: File) => void;
  startRecording: () => void;
  stopRecording: () => void;
  restart: () => void;
  setActiveAudioType: (type: "original" | "isolated") => void;
  playResult: () => void;
  setShowLimitReachedOverlay: (show: boolean) => void;
  setError: (error: string | null) => void;
}

export function useVoiceIsolate(): UseVoiceIsolateReturn {
  const [step, setStep] = useState<VoiceIsolateStep>("input");
  const [duration, setDuration] = useState<number | null>(0);
  const [isIsolating, setIsIsolating] = useState(false);
  const [originalAudioUrl, setOriginalAudioUrl] = useState<string | null>(null);
  const [isolatedAudioUrl, setIsolatedAudioUrl] = useState<string | null>(null);
  const [activeAudioType, setActiveAudioType] = useState<"original" | "isolated">("isolated");
  const [showLimitReachedOverlay, setShowLimitReachedOverlay] = useState(false);
  const { error, setError } = useAutoDismissError();

  const audioUrlRef = useRef<string | null>(null);
  const durationRef = useRef<number | null>(0);
  const audioRecorderRef = useRef<AudioRecorder | null>(null);

  if (!audioRecorderRef.current) {
    audioRecorderRef.current = new AudioRecorder({
      maxDurationSecs: MAX_RECORDING_DURATION,
      onMaxDurationCb: () => captureEvent(EventName.mw_voice_isolator_recording_max_duration_reached),
      onUpdate: ({ recordedAudioDurationSecs }) => {
        durationRef.current = recordedAudioDurationSecs;
        setDuration(recordedAudioDurationSecs);
      },
    });
  }

  const setFileUrl = useCallback((newFile: File | null) => {
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
    }
    audioUrlRef.current = newFile ? URL.createObjectURL(newFile) : null;
  }, []);

  const { togglePlay, playingId, stop: stopAudio } = useNativeAudio();
  const t = useTranslations("ClientKeys.VoiceIsolatorV2");
  const tErrors = useTranslations("ClientKeys.Errors");
  const tMicrophoneErrors = useTranslations("ClientKeys.Errors.Microphone");

  const isolate = async (targetFile: File) => {
    if (isIsolating) return;

    const inputType = targetFile.name.startsWith("recording.") ? "microphone" : "upload";
    captureEvent(EventName.mw_voice_isolator_isolation_started, {
      source_type: inputType,
      file_type: targetFile.type.startsWith("video/") ? "video" : "audio",
      file_size_bytes: targetFile.size,
    });

    setError(null);
    setIsIsolating(true);
    setStep("isolating");

    try {
      const blob = await generateVoiceIsolation({
        config: { referenceAudio: targetFile },
      });

      if (!blob) {
        throw new Error("voice_isolation_empty_response");
      }

      // Convert isolated audio blob to base64/URL
      const isolatedUrl = URL.createObjectURL(blob);

      // Use existing URL or create new one for original audio
      const originalUrl = audioUrlRef.current || URL.createObjectURL(targetFile);
      // Clear the ref since we're moving the URL to state (prevents double-revoke)
      audioUrlRef.current = null;

      captureEvent(EventName.mw_voice_isolator_isolation_succeeded, {
        source_type: inputType,
        file_type: targetFile.type.startsWith("video/") ? "video" : "audio",
        file_size_bytes: targetFile.size,
      });

      setOriginalAudioUrl(originalUrl);
      setIsolatedAudioUrl(isolatedUrl);
      setStep("result");
    } catch (error) {
      logger.error(error instanceof Error ? error : null, "Voice isolation failed");

      const errorStatus = getErrorStatusFromUnknownError(error);

      if (errorStatus === "quota_exceeded") {
        captureEvent(EventName.mw_voice_isolator_quota_exceeded, {
          source_type: inputType,
          file_type: targetFile.type.startsWith("video/") ? "video" : "audio",
          file_size_bytes: targetFile.size,
        });
        setShowLimitReachedOverlay(true);
      } else {
        captureEvent(EventName.mw_voice_isolator_isolation_failed, {
          source_type: inputType,
          file_type: targetFile.type.startsWith("video/") ? "video" : "audio",
          file_size_bytes: targetFile.size,
          error_message: error instanceof Error ? error.message : "Unknown error",
        });
        setError(t("isolation_error_description"));
      }
      // Go back to input on error
      setFileUrl(null);
      setStep("input");
    } finally {
      setIsIsolating(false);
    }
  };

  const handleFileSelected = (selectedFile: File) => {
    if (selectedFile.size > MAX_UPLOAD_SIZE_BYTES) {
      setError(
        tErrors("FileUpload.file_too_large_description", {
          maxSize: MAX_UPLOAD_SIZE_BYTES / (1024 * 1024),
        }),
      );
      return;
    }

    setError(null);
    captureEvent(EventName.mw_voice_isolator_file_uploaded, {
      source_type: "upload",
      file_type: selectedFile.type.startsWith("video/") ? "video" : "audio",
      file_size_bytes: selectedFile.size,
    });

    setFileUrl(selectedFile);
    isolate(selectedFile);
  };

  // Set up recorder callbacks
  useEffect(() => {
    if (audioRecorderRef.current === null) return;
    const recorder = audioRecorderRef.current;

    const handleStart = () => {
      captureEvent(EventName.mw_voice_isolator_recording_started);
      setStep("recording");
    };

    const handleStop = () => {
      const duration = durationRef.current ?? 0;
      const blob = recorder.getBlob();

      // Empty recording (cancelled or reset) - go back to input
      if (blob.size === 0) {
        setFileUrl(null);
        setDuration(0);
        recorder.reset();
        setStep("input");
        return;
      }

      captureEvent(EventName.mw_voice_isolator_recording_stopped, {
        recording_duration_secs: duration,
      });

      const extension = recorder.getExtension();
      const recordedFile = new File([recorder.getBlob()], `recording.${extension}`, {
        type: recorder.getMimeType() || "audio/webm",
      });
      setFileUrl(recordedFile);
      recorder.reset();
      isolate(recordedFile);
    };

    const handleError = (error: Error) => {
      handleMicrophoneError(error, {
        handler: (_title, description) => setError(description),
        t: tMicrophoneErrors,
        demoType: "voice_isolator",
      });
    };

    recorder.onStart = handleStart;
    recorder.onStop = handleStop;
    recorder.onError = handleError;

    return () => {
      if (recorder.onStart === handleStart) {
        recorder.onStart = null;
      }
      if (recorder.onStop === handleStop) {
        recorder.onStop = null;
      }
      if (recorder.onError === handleError) {
        recorder.onError = null;
      }
    };
  }, [t, tMicrophoneErrors, setFileUrl]);

  // Cleanup recorder on unmount
  // Cleanup audio URLs on unmount
  useEffect(() => {
    return () => {
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
      }
      if (originalAudioUrl) {
        URL.revokeObjectURL(originalAudioUrl);
      }
      if (isolatedAudioUrl) {
        URL.revokeObjectURL(isolatedAudioUrl);
      }
      audioRecorderRef.current?.stopRecording();
    };
  }, [originalAudioUrl, isolatedAudioUrl]);

  const startRecording = () => {
    setError(null);
    audioRecorderRef.current?.startRecording();
  };

  const restart = () => {
    captureEvent(EventName.mw_voice_isolator_restart_clicked, {
      audio_type: activeAudioType,
    });
    stopAudio();

    const recorder = audioRecorderRef.current;
    if (recorder?.recordingInfo.isRecording) {
      // Reset duration so handleStop knows to discard
      durationRef.current = 0;
      setDuration(0);
      recorder.stopRecording();
      return;
    }

    // Not recording, reset all state
    setFileUrl(null);
    setDuration(0);
    setOriginalAudioUrl(null);
    setIsolatedAudioUrl(null);
    setActiveAudioType("isolated");
    setShowLimitReachedOverlay(false);
    setError(null);
    recorder?.reset();
    setStep("input");
  };

  const stopRecording = () => {
    audioRecorderRef.current?.stopRecording();
  };

  const playResult = () => {
    const url = activeAudioType === "isolated" ? isolatedAudioUrl : originalAudioUrl;
    if (!url) return;

    captureEvent(EventName.mw_voice_isolator_playback_toggled, {
      audio_type: activeAudioType,
      target_state: playingId === url ? "paused" : "playing",
    });

    togglePlay({
      id: url,
      url: url,
      loop: false,
      trackingProps: {
        source: "voice_isolator_v2",
        audio_type: activeAudioType,
      },
    });
  };

  const isPlaying =
    Boolean(playingId) &&
    (playingId === audioUrlRef.current || playingId === originalAudioUrl || playingId === isolatedAudioUrl);

  return {
    // State
    step,
    duration,
    isPlaying,
    isIsolating,
    originalAudioUrl,
    isolatedAudioUrl,
    activeAudioType,
    showLimitReachedOverlay,
    error,

    // Actions
    handleFileSelected,
    startRecording,
    stopRecording,
    restart,
    setActiveAudioType,
    playResult,
    setShowLimitReachedOverlay,
    setError,
  };
}
